import { Link } from "react-router";
import type { PostSummary } from "../../api/types";
import { scoreColor } from "../../theme/colors";
import { VideoPlayer } from "../media/VideoPlayer";
import { FeedbackButtons } from "../FeedbackButtons";

interface Props {
  post: PostSummary;
}

export function PostCard({ post }: Props) {
  const isReel = post.is_video;
  const isCarousel = post.slide_count > 1 && !isReel;

  return (
    <Link
      to={`/posts/${post.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition hover:shadow-md"
    >
      <div className="bg-brand-text-dark">
        {isReel && post.image_paths[0] ? (
          <VideoPlayer src={post.image_paths[0]} vertical className="rounded-none" />
        ) : isCarousel ? (
          <div className="flex gap-1 overflow-x-auto p-1">
            {post.image_paths.map((p, i) => (
              <img
                key={i}
                src={p}
                alt={`slide ${i + 1}`}
                className="h-64 w-auto rounded object-contain"
              />
            ))}
          </div>
        ) : (
          <img
            src={post.image_paths[0] ?? ""}
            alt={post.topic}
            className="h-64 w-full object-contain"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Pill text={post.category} background="#0000FF" />
          {post.template ? <Pill text={post.template} background="#FF00FF" /> : null}
          {post.style ? <Pill text={post.style} background="#9B00FF" /> : null}
          {isCarousel ? (
            <Pill text={`${post.slide_count} slides`} background="#1A1A1A" />
          ) : null}
          {post.brand_score != null ? (
            <Pill
              text={`score ${post.brand_score}`}
              background={scoreColor(post.brand_score)}
            />
          ) : null}
          {/* Always show approval state — previously pending posts had no pill,
              so you couldn't tell at a glance which ones had been touched. */}
          <Pill
            text={post.approval.status}
            background={
              post.approval.status === "published"
                ? "#1B8F3A"
                : post.approval.status === "approved"
                  ? "#0066CC"
                  : post.approval.status === "rejected"
                    ? "#C03030"
                    : "#C49A00" /* pending = warm amber */
            }
          />
          {/* Trending badge when the post came from a viral signal (Telegram /
              Google Trends) rather than an editorial pick. */}
          {post.trending_origin ? (
            <span
              title={
                post.trending_origin.virality_note ??
                post.trending_origin.amplification ??
                "Trending signal"
              }
              className="inline-flex items-center gap-1 rounded-full bg-brand-magenta/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-magenta"
            >
              <span aria-hidden>🔥</span>
              viral
            </span>
          ) : null}
        </div>
        <p className="line-clamp-2 text-sm font-bold text-brand-text-dark">
          {post.topic || post.caption_feed.slice(0, 80)}
        </p>
        <p className="line-clamp-3 text-sm text-brand-text-dark/70">
          {post.caption_feed}
        </p>
        <div className="mt-auto flex items-end justify-between gap-3 border-t border-black/5 pt-3">
          {post.source ? (
            <p className="flex-1 text-[11px] uppercase tracking-wider text-brand-text-dark/50">
              {post.source}
            </p>
          ) : (
            <span />
          )}
          <FeedbackButtons postId={post.id} feedback={post.feedback} variant="compact" />
        </div>
      </div>
    </Link>
  );
}

function Pill({ text, background }: { text: string; background: string }) {
  return (
    <span
      className="inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
      style={{ background }}
    >
      {text}
    </span>
  );
}
